/**
 * Bulk Progress Modal Component
 *
 * Displays real-time progress during bulk operations on expense reports.
 * Non-dismissible during processing; becomes dismissible after completion.
 * Supports "Retry Failed" functionality.
 */

import { renderIcon } from '../utils/icons';

/**
 * Progress status for a single report in a bulk operation
 */
export interface BulkOperationProgress {
  reportId: string;
  reportName: string;
  status: 'pending' | 'processing' | 'success' | 'failed';
  error?: string;
  result?: unknown;
}

/**
 * Operation types for bulk actions
 */
export type BulkOperationType = 'quickbooks' | 'epo' | 'paid';

/**
 * Labels for each operation type
 */
const OPERATION_LABELS: Record<BulkOperationType, string> = {
  quickbooks: 'Book in QuickBooks',
  epo: 'Generate EPO',
  paid: 'Mark as Paid',
};

/**
 * Callback for processing a single report in a bulk operation
 */
export type BulkOperationHandler = (reportId: string) => Promise<void>;

/**
 * Bulk Progress Modal class
 *
 * Manages the display of bulk operation progress with real-time updates.
 */
export class BulkProgressModal {
  private overlay: HTMLDivElement;
  private items: Map<string, BulkOperationProgress>;
  private operationType: BulkOperationType;
  private isProcessing = false;
  private boundKeyHandler: (e: KeyboardEvent) => void;
  private onRetry: ((failedIds: string[]) => void) | null;

  constructor(
    operationType: BulkOperationType,
    reports: Array<{ id: string; name: string }>,
    onRetry?: (failedIds: string[]) => void
  ) {
    this.operationType = operationType;
    this.onRetry = onRetry ?? null;
    this.items = new Map(
      reports.map((r) => [
        r.id,
        {
          reportId: r.id,
          reportName: r.name,
          status: 'pending' as const,
        },
      ])
    );

    this.boundKeyHandler = (e: KeyboardEvent) => {
      // Only allow escape when not processing
      if (e.key === 'Escape' && !this.isProcessing) {
        this.close();
      }
    };

    this.overlay = this.createElement();
  }

  /**
   * Create the modal DOM elements
   */
  private createElement(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className =
      'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm opacity-0 transition-opacity duration-200';

    // Prevent backdrop click from closing during processing
    overlay.addEventListener('click', (e) => {
      if (!this.isProcessing && e.target === overlay) {
        this.close();
      }
    });

    const modal = document.createElement('div');
    modal.className =
      'relative w-full max-w-lg mx-4 bg-card border border-border rounded-xl shadow-2xl flex flex-col max-h-[80vh]';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'bulk-progress-title');

    // Header
    const header = document.createElement('div');
    header.className = 'flex items-center justify-between px-6 py-4 border-b border-border/50 flex-shrink-0';

    const title = document.createElement('h2');
    title.id = 'bulk-progress-title';
    title.className = 'text-base font-semibold text-foreground';
    title.textContent = OPERATION_LABELS[this.operationType];
    header.appendChild(title);

    // Close button (hidden during processing)
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className =
      'close-btn p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors hidden';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.appendChild(renderIcon('x', { size: 'sm' }));
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);

    modal.appendChild(header);

    // Scrollable item list
    const listContainer = document.createElement('div');
    listContainer.className = 'progress-list flex-1 overflow-y-auto px-6 py-4 space-y-2 min-h-0';
    listContainer.setAttribute('aria-live', 'polite');
    listContainer.setAttribute('aria-atomic', 'false');
    modal.appendChild(listContainer);

    // Summary area (hidden until complete)
    const summary = document.createElement('div');
    summary.className = 'summary-area hidden px-6 py-3 bg-muted/20 border-t border-border/50 flex-shrink-0';
    summary.setAttribute('aria-live', 'polite');
    modal.appendChild(summary);

    // Footer with action buttons
    const footer = document.createElement('div');
    footer.className = 'footer-area hidden px-6 py-4 border-t border-border/50 flex justify-end gap-3 flex-shrink-0';

    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.className =
      'retry-btn inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors hidden';
    retryBtn.appendChild(renderIcon('refresh', { size: 'sm' }));
    retryBtn.appendChild(document.createTextNode(' Retry Failed'));
    retryBtn.addEventListener('click', () => this.handleRetry());
    footer.appendChild(retryBtn);

    const closeFooterBtn = document.createElement('button');
    closeFooterBtn.type = 'button';
    closeFooterBtn.className =
      'close-footer-btn inline-flex items-center px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-accent-600 transition-colors';
    closeFooterBtn.textContent = 'Close';
    closeFooterBtn.addEventListener('click', () => this.close());
    footer.appendChild(closeFooterBtn);

    modal.appendChild(footer);
    overlay.appendChild(modal);

    // Render initial item list
    this.renderItems(listContainer);

    return overlay;
  }

  /**
   * Render the list of items with their current status
   */
  private renderItems(container: HTMLElement): void {
    container.innerHTML = '';

    for (const item of this.items.values()) {
      const row = document.createElement('div');
      row.className = 'flex items-center gap-3 py-2';
      row.setAttribute('data-report-id', item.reportId);

      // Status icon
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'flex-shrink-0 w-5 h-5 flex items-center justify-center';
      iconWrapper.setAttribute('data-role', 'status-icon');
      iconWrapper.appendChild(this.createStatusIcon(item.status));
      row.appendChild(iconWrapper);

      // Report name
      const nameDiv = document.createElement('div');
      nameDiv.className = 'flex-1 min-w-0';
      nameDiv.setAttribute('data-role', 'report-name');

      const nameSpan = document.createElement('span');
      nameSpan.className = 'text-sm text-foreground truncate block';
      nameSpan.textContent = item.reportName;
      nameDiv.appendChild(nameSpan);

      if (item.status === 'failed' && item.error) {
        const errorSpan = document.createElement('span');
        errorSpan.className = 'text-xs text-destructive block mt-0.5';
        errorSpan.textContent = item.error;
        nameDiv.appendChild(errorSpan);
      }

      row.appendChild(nameDiv);

      container.appendChild(row);
    }
  }

  /**
   * Create a status icon for the given status
   */
  private createStatusIcon(status: BulkOperationProgress['status']): Element {
    switch (status) {
      case 'pending': {
        return renderIcon('clock', { size: 'sm', className: 'text-muted-foreground' });
      }
      case 'processing': {
        const spinner = document.createElement('div');
        spinner.className = 'w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin';
        spinner.setAttribute('aria-label', 'Processing');
        return spinner;
      }
      case 'success': {
        const icon = renderIcon('checkCircle', { size: 'sm', className: 'text-success' });
        icon.setAttribute('aria-label', 'Success');
        return icon;
      }
      case 'failed': {
        const icon = renderIcon('xCircle', { size: 'sm', className: 'text-destructive' });
        icon.setAttribute('aria-label', 'Failed');
        return icon;
      }
    }
  }

  /**
   * Update the status icon for a specific item in-place
   */
  private updateItemRow(reportId: string): void {
    const item = this.items.get(reportId);
    if (!item) return;

    const row = this.overlay.querySelector(`[data-report-id="${reportId}"]`);
    if (!row) return;

    // Update icon
    const iconWrapper = row.querySelector('[data-role="status-icon"]');
    if (iconWrapper) {
      iconWrapper.innerHTML = '';
      iconWrapper.appendChild(this.createStatusIcon(item.status));
    }

    // Update name/error section
    const nameDiv = row.querySelector('[data-role="report-name"]');
    if (nameDiv) {
      // Remove existing error span if any
      const existingError = nameDiv.querySelector('.text-destructive');
      if (existingError) existingError.remove();

      // Add error span if failed
      if (item.status === 'failed' && item.error) {
        const errorSpan = document.createElement('span');
        errorSpan.className = 'text-xs text-destructive block mt-0.5';
        errorSpan.textContent = item.error;
        nameDiv.appendChild(errorSpan);
      }
    }

    // Announce status change for screen readers
    const listContainer = this.overlay.querySelector('.progress-list');
    if (listContainer) {
      listContainer.setAttribute('aria-label', `${item.reportName}: ${item.status}`);
    }
  }

  /**
   * Open the modal and mount it to the document body
   */
  open(): void {
    document.body.appendChild(this.overlay);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.boundKeyHandler);

    // Animate in
    requestAnimationFrame(() => {
      this.overlay.classList.remove('opacity-0');
      this.overlay.classList.add('opacity-100');
    });

    // Focus the modal for accessibility
    const modal = this.overlay.querySelector('[role="dialog"]') as HTMLElement || this.overlay;
    modal.setAttribute('tabindex', '-1');
    requestAnimationFrame(() => modal.focus());
  }

  /**
   * Close the modal and remove it from the DOM
   */
  close(): void {
    if (this.isProcessing) return;

    document.removeEventListener('keydown', this.boundKeyHandler);

    this.overlay.classList.remove('opacity-100');
    this.overlay.classList.add('opacity-0');

    setTimeout(() => {
      if (this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
      document.body.style.overflow = '';
    }, 200);
  }

  /**
   * Force close the modal regardless of processing state.
   * Used when the parent view is destroyed to prevent scroll lock.
   */
  forceClose(): void {
    this.isProcessing = false;
    document.removeEventListener('keydown', this.boundKeyHandler);
    if (this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    document.body.style.overflow = '';
  }

  /**
   * Update the status of a specific report
   */
  updateItemStatus(reportId: string, status: BulkOperationProgress['status'], error?: string): void {
    const item = this.items.get(reportId);
    if (!item) return;

    item.status = status;
    if (error !== undefined) {
      item.error = error;
    }

    this.updateItemRow(reportId);
  }

  /**
   * Mark an item as currently processing
   */
  startItem(reportId: string): void {
    this.updateItemStatus(reportId, 'processing');
  }

  /**
   * Mark an item as successfully completed
   */
  completeItem(reportId: string, result?: unknown): void {
    const item = this.items.get(reportId);
    if (item) {
      item.result = result;
    }
    this.updateItemStatus(reportId, 'success');
  }

  /**
   * Mark an item as failed
   */
  failItem(reportId: string, error: string): void {
    this.updateItemStatus(reportId, 'failed', error);
  }

  /**
   * Set processing state (controls dismiss behavior)
   */
  setProcessing(processing: boolean): void {
    this.isProcessing = processing;

    const closeBtn = this.overlay.querySelector('.close-btn');
    if (closeBtn) {
      if (processing) {
        closeBtn.classList.add('hidden');
      } else {
        closeBtn.classList.remove('hidden');
      }
    }
  }

  /**
   * Show completion summary after all items are processed
   */
  showCompletion(): void {
    this.isProcessing = false;

    let successCount = 0;
    let failedCount = 0;
    const failedIds: string[] = [];

    for (const item of this.items.values()) {
      if (item.status === 'success') {
        successCount++;
      } else if (item.status === 'failed') {
        failedCount++;
        failedIds.push(item.reportId);
      }
    }

    // Show summary
    const summaryArea = this.overlay.querySelector('.summary-area');
    if (summaryArea) {
      summaryArea.classList.remove('hidden');
      const successText = successCount > 0 ? `${successCount} succeeded` : '';
      const failedText = failedCount > 0 ? `${failedCount} failed` : '';
      const summaryText = [successText, failedText].filter(Boolean).join(', ') || 'No items processed';
      summaryArea.textContent = summaryText;
    }

    // Show footer with buttons
    const footerArea = this.overlay.querySelector('.footer-area');
    if (footerArea) {
      footerArea.classList.remove('hidden');
    }

    // Show close button in header
    const closeBtn = this.overlay.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.classList.remove('hidden');
    }

    // Show retry button only if there are failures and retry handler is provided
    if (failedCount > 0 && this.onRetry) {
      const retryBtn = this.overlay.querySelector('.retry-btn');
      if (retryBtn) {
        retryBtn.classList.remove('hidden');
      }
    }

    // Announce completion for screen readers
    const summaryArea2 = this.overlay.querySelector('.summary-area');
    if (summaryArea2) {
      summaryArea2.setAttribute('aria-live', 'assertive');
    }
  }

  /**
   * Show EPO download confirmation message in the summary area.
   * Called after a successful bulk EPO ZIP download.
   */
  showEpoDownloadConfirmation(zipFilename: string): void {
    this.isProcessing = false;

    const summaryArea = this.overlay.querySelector('.summary-area');
    if (summaryArea) {
      summaryArea.classList.remove('hidden');
      summaryArea.textContent = `Downloaded ${zipFilename}`;
      summaryArea.setAttribute('aria-live', 'assertive');
    }

    const footerArea = this.overlay.querySelector('.footer-area');
    if (footerArea) {
      footerArea.classList.remove('hidden');
    }

    const closeBtn = this.overlay.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.classList.remove('hidden');
    }
  }

  /**
   * Handle retry of failed items
   */
  private handleRetry(): void {
    const failedIds: string[] = [];
    for (const item of this.items.values()) {
      if (item.status === 'failed') {
        failedIds.push(item.reportId);
      }
    }

    if (failedIds.length > 0 && this.onRetry) {
      // Reset failed items to pending
      for (const id of failedIds) {
        const item = this.items.get(id);
        if (item) {
          item.status = 'pending';
          delete item.error;
          this.updateItemRow(id);
        }
      }

      // Hide footer and summary while retrying
      const footerArea = this.overlay.querySelector('.footer-area');
      if (footerArea) footerArea.classList.add('hidden');

      const summaryArea = this.overlay.querySelector('.summary-area');
      if (summaryArea) summaryArea.classList.add('hidden');

      const closeBtn = this.overlay.querySelector('.close-btn');
      if (closeBtn) closeBtn.classList.add('hidden');

      this.isProcessing = true;
      this.onRetry(failedIds);
    }
  }

  /**
   * Get all report IDs in this modal
   */
  getReportIds(): string[] {
    return Array.from(this.items.keys());
  }

  /**
   * Get failed report IDs
   */
  getFailedIds(): string[] {
    return Array.from(this.items.values())
      .filter((item) => item.status === 'failed')
      .map((item) => item.reportId);
  }

  /**
   * Run a bulk operation with automatic progress tracking
   *
   * @param handler - Async function to process each report
   */
  async runOperation(handler: BulkOperationHandler): Promise<void> {
    this.setProcessing(true);

    for (const reportId of this.items.keys()) {
      this.startItem(reportId);

      try {
        await handler(reportId);
        this.completeItem(reportId);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        this.failItem(reportId, errorMessage);
      }
    }

    this.showCompletion();
  }
}
